import { useMemo, useState, type DragEvent } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { ETAPES, ETAPE_INFO, capaciteEtape, cibleDe, conseilEtape, etapesNonConsecutives, plageDe, previsionTables, tailleGroupeAttendue, type AffectationHote, type Alerte, type Etape, type Foyer } from '@dinertournant/core';
import { BandeauAlerte, CarteHote, IconeActualiser, IconeMaison, PastilleEtape } from '../../components/index.js';
import { useChargement } from '../../hooks/useChargement.js';
import { api, apiPatch, heureCourte, type Evenement } from '../../lib/api.js';
import type { ContexteEvenement } from './EvenementLayout.js';

interface DonneesHotes {
  volontaires: Foyer[];
  hotes: AffectationHote[];
  participants: number;
  tailleMin: number;
  tailleMax: number;
  bornes: { minMax: number | null; maxMax: number | null; hotesParEtape: number };
  hotesIdeal: number;
  alertes: Alerte[];
}

interface ReponseHotes {
  hotes: AffectationHote[];
  alertes: Alerte[];
}

const AU: Record<Etape, string> = { entree: "à l'entrée", plat: 'au plat', dessert: 'au dessert' };
const LE: Record<Etape, string> = { entree: "l'entrée", plat: 'le plat', dessert: 'le dessert' };

/** Jauge de taille de groupe : plage min–max surlignée, repère sur la taille visée, point sur la prévision. */
function JaugeTaille({ taille, plage: plageReglee }: { taille: number; plage: { min: number; max: number } }) {
  const plage = plageDe(plageReglee);
  const cible = cibleDe(plageReglee);
  const debut = Math.max(2, plage.min - 1);
  const fin = plage.max + 2;
  const pos = (v: number) => `${((Math.min(Math.max(v, debut), fin) - debut) / (fin - debut)) * 100}%`;
  const hors = taille < plage.min || taille > plage.max;
  const graduations = Array.from({ length: fin - debut + 1 }, (_, i) => debut + i);
  return (
    <>
      <div className="jauge-taille" role="img" aria-label={`Taille de groupe ${taille}, plage conseillée ${plage.min} à ${plage.max}, cible ${cible}`}>
        <div className="rail" />
        <div className="plage" style={{ left: pos(plage.min), width: `calc(${pos(plage.max)} - ${pos(plage.min)})` }} />
        <div className="cible" style={{ left: pos(cible) }} />
        <div className={`point${hors ? ' hors' : ''}`} style={{ left: pos(taille) }} />
      </div>
      <div className="jauge-legende" aria-hidden="true">
        {graduations.map((g) => (
          <span key={g}>{g === cible ? `cible ${g}` : g === fin ? `${g}+` : g}</span>
        ))}
      </div>
    </>
  );
}

/** Écran 4 : hôtes par étape. Proposition automatique, glisser-déposer doublé d'un menu clavier, états d'alerte. */
export function Hotes() {
  const { detail, recharger: rechargerEvenement } = useOutletContext<ContexteEvenement>();
  const eventId = detail.event.id;
  const { donnees, erreur, remplacer, recharger } = useChargement(() => api.get<DonneesHotes>(`/api/events/${eventId}/hosts`), [eventId]);
  const [erreurAction, setErreurAction] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [survol, setSurvol] = useState<Etape | 'reserve' | null>(null);
  const [glisse, setGlisse] = useState<string | null>(null);
  const [ignorees, setIgnorees] = useState<Set<string>>(new Set());
  const [designation, setDesignation] = useState<Etape | null>(null);
  const [recalage, setRecalage] = useState(false);

  const hotes = donnees?.hotes ?? [];
  const volontaires = donnees?.volontaires ?? [];
  const participants = donnees?.participants ?? 0;
  const ideal = donnees?.hotesIdeal ?? 1;
  const plageReglee = { min: donnees?.tailleMin ?? detail.event.tailleMin ?? 3, max: donnees?.tailleMax ?? detail.event.tailleMax ?? 5 };
  const plage = plageDe(plageReglee);
  const cible = cibleDe(plageReglee);
  const bornes = donnees?.bornes ?? { minMax: null, maxMax: null, hotesParEtape: 1 };
  const foyersDe = (e: Etape): Foyer[] => hotesDe(e).map((h) => parId.get(h.foyerId)).filter((f): f is Foyer => !!f);
  const horaires: Record<Etape, string> = { entree: detail.event.horaireEntree, plat: detail.event.horairePlat, dessert: detail.event.horaireDessert };

  const parId = useMemo(() => new Map(volontaires.map((f) => [f.id, f])), [volontaires]);
  const etapeDe = (id: string) => hotes.find((h) => h.foyerId === id);
  /** Étapes où une voiture reçoit (deux en cas d'exception forcée). */
  const etapesDe = (id: string): Etape[] => ETAPES.filter((e) => hotes.some((h) => h.foyerId === id && h.etape === e));
  const hotesDe = (e: Etape) => hotes.filter((h) => h.etape === e);
  const reserveDe = (e: Etape) => volontaires.filter((f) => f.disponibilites.includes(e) && !etapeDe(f.id));
  const nonPlaces = volontaires.filter((f) => !etapeDe(f.id));
  const horsDispo = hotes.filter((h) => {
    const f = parId.get(h.foyerId);
    return f && !f.disponibilites.includes(h.etape) && !h.accordHorsDispo;
  });
  const tailleTypique = tailleGroupeAttendue(participants, Math.round(hotes.length / 3) || 1);

  const enregistrer = async (nouveaux: AffectationHote[]) => {
    if (!donnees) return;
    setErreurAction(null);
    setEnCours(true);
    try {
      const r = await api.put<ReponseHotes>(`/api/events/${eventId}/hosts`, nouveaux);
      remplacer({ ...donnees, hotes: r.hotes, alertes: r.alertes });
      void rechargerEvenement();
    } catch (e) {
      setErreurAction((e as Error).message);
      await recharger();
    } finally {
      setEnCours(false);
    }
  };

  /**
   * Déplace une voiture vers une étape (ou la retire : null). Sans `depuis`, toutes ses réceptions sont remplacées
   * (règle 4 : une seule étape). Avec `depuis`, seule la réception de cette colonne bouge : une voiture forcée
   * à deux étapes garde l'autre.
   */
  const deplacer = (foyerId: string, etape: Etape | null, depuis?: Etape, accord = false) => {
    if (!depuis) {
      const autres = hotes.filter((h) => h.foyerId !== foyerId);
      void enregistrer(etape ? [...autres, { foyerId, etape, accordHorsDispo: accord }] : autres);
      return;
    }
    if (etape && etape !== depuis && etapesDe(foyerId).includes(etape)) {
      setErreurAction(`${parId.get(foyerId)?.nom ?? 'Cette voiture'} reçoit déjà ${AU[etape]}.`);
      return;
    }
    const restantes = hotes.filter((h) => h.foyerId === foyerId && h.etape !== depuis);
    const deux = restantes.length + (etape ? 1 : 0) >= 2;
    const autres = hotes.filter((h) => h.foyerId !== foyerId);
    const miennes = restantes.map((h) => ({ ...h, accordDeuxEtapes: deux }));
    void enregistrer(etape ? [...autres, ...miennes, { foyerId, etape, accordHorsDispo: false, accordDeuxEtapes: deux }] : [...autres, ...miennes]);
  };

  /** Exception forcée : la voiture reçoit aussi à une 2e étape, avec son accord (vaut accord hors disponibilités). */
  const recevoirAussi = (foyerId: string, etape: Etape) => {
    const f = parId.get(foyerId);
    const actuelles = etapesDe(foyerId);
    if (!f || actuelles.length !== 1 || actuelles.includes(etape)) return;
    const toutes = [...actuelles, etape];
    const libelles = ETAPES.filter((e) => toutes.includes(e)).map((e) => ETAPE_INFO[e].label.toLowerCase()).join(' et ');
    const conseil = etapesNonConsecutives(toutes) ? `\n\nAttention : ces étapes ne se suivent pas. ${f.nom} devra partir au plat chez une autre voiture, puis rentrer avant ses invités du dessert.` : '';
    const message = `Forcer ${f.nom} à recevoir à deux étapes (${libelles}) ?\n\nUne voiture ne reçoit normalement qu'une fois : assurez-vous de son accord avant de valider.${conseil}`;
    if (!window.confirm(message)) return;
    const autres = hotes.filter((h) => h.foyerId !== foyerId);
    const miennes = hotes.filter((h) => h.foyerId === foyerId).map((h) => ({ ...h, accordDeuxEtapes: true }));
    // La confirmation vaut accord : y compris pour une étape hors de ses disponibilités.
    void enregistrer([...autres, ...miennes, { foyerId, etape, accordHorsDispo: !f.disponibilites.includes(etape), accordDeuxEtapes: true }]);
  };
  const accorder = (foyerId: string) => void enregistrer(hotes.map((h) => (h.foyerId === foyerId ? { ...h, accordHorsDispo: true } : h)));

  const changerTailles = async (tailles: { tailleMin: number; tailleMax: number }) => {
    if (tailles.tailleMin > tailles.tailleMax) {
      setErreurAction('Le minimum doit être inférieur ou égal au maximum.');
      return;
    }
    setErreurAction(null);
    setEnCours(true);
    try {
      await apiPatch<{ event: Evenement }>(`/api/events/${eventId}/tailles`, tailles);
      await recharger();
      void rechargerEvenement();
      setRecalage(hotes.length > 0);
    } catch (e) {
      setErreurAction((e as Error).message);
    } finally {
      setEnCours(false);
    }
  };

  const nouvelleProposition = async () => {
    if (!donnees) return;
    setErreurAction(null);
    setEnCours(true);
    try {
      const r = await api.post<ReponseHotes>(`/api/events/${eventId}/hosts/proposition`, { graine: `${Date.now()}` });
      remplacer({ ...donnees, hotes: r.hotes, alertes: r.alertes });
      setIgnorees(new Set());
      setRecalage(false);
      void rechargerEvenement();
    } catch (e) {
      setErreurAction((e as Error).message);
    } finally {
      setEnCours(false);
    }
  };

  /* --- glisser-déposer (HTML5), doublé du menu « Déplacer vers… » de CarteHote --- */
  const onDragStart = (e: DragEvent, foyerId: string, depuis?: Etape) => {
    e.dataTransfer.setData('text/plain', depuis ? `${foyerId}|${depuis}` : foyerId);
    e.dataTransfer.effectAllowed = 'move';
    setGlisse(depuis ? `${foyerId}|${depuis}` : foyerId);
  };
  const onDrop = (e: DragEvent, cible: Etape | null) => {
    e.preventDefault();
    const donnee = e.dataTransfer.getData('text/plain') || glisse;
    setSurvol(null);
    setGlisse(null);
    if (!donnee) return;
    const [foyerId, depuis] = donnee.split('|') as [string, Etape | undefined];
    if (depuis ? depuis === cible : (etapeDe(foyerId)?.etape ?? null) === cible) return;
    deplacer(foyerId, cible, depuis);
  };

  /** Étape à conseiller pour un volontaire non placé : parmi ses disponibilités, la moins pourvue. */
  const meilleureEtape = (f: Foyer): Etape | null => {
    const possibles = ETAPES.filter((e) => f.disponibilites.includes(e));
    if (possibles.length === 0) return null;
    return possibles.sort((a, b) => hotesDe(a).length - hotesDe(b).length)[0] as Etape;
  };

  const alertes = (donnees?.alertes ?? []).filter((a) => !ignorees.has(`${a.code}:${a.etape ?? ''}:${a.foyerId ?? ''}`));
  const ignorer = (a: Alerte) => setIgnorees(new Set([...ignorees, `${a.code}:${a.etape ?? ''}:${a.foyerId ?? ''}`]));
  const pret = alertes.find((a) => a.code === 'pret');
  const reserves = useMemo(() => {
    const items: { niveau: 'attention' | 'info'; texte: string }[] = [];
    const grandes = ETAPES.filter((e) => hotesDe(e).length > 0 && tailleGroupeAttendue(participants, hotesDe(e).length) > cible);
    if (grandes.length) items.push({ niveau: 'attention', texte: `${grandes.map((e) => ETAPE_INFO[e].label).join(', ').replace(/, ([^,]*)$/, ' et $1')} : groupes de ${tailleGroupeAttendue(participants, hotesDe(grandes[0]!).length)}, au-dessus de la cible de ${cible}.` });
    for (const f of nonPlaces) items.push({ niveau: 'attention', texte: `${f.nom}, volontaire, n'est pas utilisé${f.membres.length > 1 ? '' : ''}.` });
    for (const e of ETAPES) if (hotesDe(e).length > 0 && reserveDe(e).length === 0) items.push({ niveau: 'info', texte: `${ETAPE_INFO[e].label} n'a aucun volontaire de réserve : un désistement d'hôte s'y gérerait à la main.` });
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotes, volontaires, participants, cible]);

  if (erreur) return <BandeauAlerte niveau="attention" role="alert" titre={erreur} />;

  return (
    <div className="page-app">
      <div className="entete-page">
        <div style={{ maxWidth: 720 }}>
          <h1 className="display">Hôtes par étape</h1>
          <p className="texte-doux">
            
            Chaque voiture hôte reçoit à <strong>une seule</strong> étape (sauf exception que vous forcez avec son accord, via « Recevoir aussi au… »). Voici une proposition équilibrée d'après leurs disponibilités : faites glisser une carte pour la changer d'étape, ou utilisez le menu « Déplacer vers… ».
          </p>
        </div>
        <div className="ligne" style={{ flexWrap: 'wrap' }}>
          <button type="button" className="btn" onClick={nouvelleProposition} disabled={enCours || volontaires.length === 0}>
            <IconeActualiser /> Nouvelle proposition
          </button>
          <Link to={`/app/e/${eventId}/repartition`} className="btn principal" aria-disabled={!pret}>
            Répartir les convives →
          </Link>
        </div>
      </div>

      {erreurAction ? <BandeauAlerte niveau="attention" role="alert" titre={erreurAction} /> : null}
      {recalage ? (
        <BandeauAlerte
          niveau="info"
          role="status"
          titre={`Tables de ${plage.min} à ${plage.max} visées. Il faudrait ${ideal} hôte${ideal > 1 ? 's' : ''} par étape.`}
          actions={
            <>
              <button type="button" className="btn principal" onClick={nouvelleProposition} disabled={enCours}>
                Refaire la proposition d'hôtes
              </button>
              <button type="button" className="btn" onClick={() => setRecalage(false)}>
                Garder les hôtes actuels
              </button>
            </>
          }
        >
          Les hôtes déjà placés n'ont pas bougé : la taille des tables dépend de leur nombre, pas du réglage.
        </BandeauAlerte>
      ) : null}
      {detail.planning.planningObsolete ? (
        <BandeauAlerte niveau="sombre" titre={`${detail.planning.changements.length} changement${detail.planning.changements.length > 1 ? 's' : ''} depuis la répartition.`} actions={<Link className="btn" to={`/app/e/${eventId}/repartition`}>Voir la répartition</Link>}>
          Le planning n'est plus à jour. Les parcours déjà envoyés restent valables tant que vous ne régénérez pas.
        </BandeauAlerte>
      ) : null}

      <section className="carte resume-hotes" aria-label="Résumé">
        <div className="pile-6">
          <strong>
            Idéal pour {participants} participants : {ideal} hôte{ideal > 1 ? 's' : ''} par étape (tables de {plage.min} à {plage.max})
          </strong>
          <div className="ligne" style={{ gap: 10, fontSize: 15, flexWrap: 'wrap' }} role="group" aria-label="Convives par table">
            <span>Convives par table : de</span>
            <select className="f-input" style={{ width: 'auto', minHeight: 40, padding: '6px 10px' }} value={plageReglee.min} disabled={enCours} onChange={(ev) => changerTailles({ tailleMin: Number(ev.target.value), tailleMax: plageReglee.max })} aria-label="Minimum de convives par table">
              {Array.from({ length: 19 }, (_, i) => i + 2).map((n) => (
                <option key={n} value={n} disabled={n > plageReglee.max || (bornes.minMax !== null && n > bornes.minMax)}>
                  {n}
                  {bornes.minMax !== null && n > bornes.minMax ? ' (un hôte ne reçoit pas autant)' : ''}
                </option>
              ))}
            </select>
            <span>à</span>
            <select className="f-input" style={{ width: 'auto', minHeight: 40, padding: '6px 10px' }} value={plageReglee.max} disabled={enCours} onChange={(ev) => changerTailles({ tailleMin: plageReglee.min, tailleMax: Number(ev.target.value) })} aria-label="Maximum de convives par table">
              {Array.from({ length: 19 }, (_, i) => i + 2).map((n) => (
                <option key={n} value={n} disabled={n < plageReglee.min || (bornes.maxMax !== null && n > bornes.maxMax)}>
                  {n}
                  {bornes.maxMax !== null && n > bornes.maxMax ? ' (pas assez d’hôtes pour cette taille)' : ''}
                </option>
              ))}
            </select>
            <span className="texte-muet">
              hôte compris
              {bornes.maxMax !== null && bornes.minMax !== null ? ` · d'après les capacités déclarées : minimum ${bornes.minMax} au plus, maximum ${bornes.maxMax} au plus (${bornes.hotesParEtape} hôte${bornes.hotesParEtape > 1 ? 's' : ''} par étape)` : ''}
            </span>
          </div>
          <span className="texte-doux" style={{ fontSize: 15 }}>
            {volontaires.length === 0
              ? "Aucune voiture n'a encore proposé de recevoir."
              : hotes.length === 0
                ? `${volontaires.length} volontaire${volontaires.length > 1 ? 's' : ''} : cliquez sur « Nouvelle proposition » pour les répartir sur les trois étapes.`
                : `Avec ${volontaires.length} volontaire${volontaires.length > 1 ? 's' : ''}, la proposition place ${Math.min(...ETAPES.map((e) => hotesDe(e).length)) === Math.max(...ETAPES.map((e) => hotesDe(e).length)) ? Math.max(...ETAPES.map((e) => hotesDe(e).length)) : `${Math.min(...ETAPES.map((e) => hotesDe(e).length))} à ${Math.max(...ETAPES.map((e) => hotesDe(e).length))}`} hôtes par étape : des groupes de ${tailleTypique}, ${tailleTypique >= plage.min && tailleTypique <= plage.max ? 'dans' : 'hors de'} la plage conseillée de ${plage.min} à ${plage.max}.`}
          </span>
        </div>
        <div className="compteurs">
          <span>
            <strong>{hotes.length}</strong>hôtes placés
          </span>
          <span>
            <strong className={horsDispo.length ? 'attention' : ''}>{horsDispo.length}</strong>hors disponibilités
          </span>
          <span>
            <strong className={nonPlaces.length ? 'attention' : ''}>{nonPlaces.length}</strong>non utilisé{nonPlaces.length > 1 ? 's' : ''}
          </span>
        </div>
      </section>

      <div className="colonnes-etapes">
        {ETAPES.map((e) => {
          const liste = hotesDe(e);
          const prev = previsionTables(participants, foyersDe(e));
          const dispo = volontaires.filter((f) => f.disponibilites.includes(e)).length;
          return (
            <section
              key={e}
              className={`col-etape etape-${e}${survol === e ? ' survol' : ''}${liste.length === 0 ? ' vide' : ''}`}
              aria-label={ETAPE_INFO[e].label}
              onDragOver={(ev) => {
                ev.preventDefault();
                if (survol !== e) setSurvol(e);
              }}
              onDragLeave={() => setSurvol(null)}
              onDrop={(ev) => onDrop(ev, e)}
            >
              <header>
                <PastilleEtape etape={e} variante="numero" grande detail={heureCourte(horaires[e])} />
                <span className="dispo">
                  {dispo} disponible{dispo > 1 ? 's' : ''}
                </span>
              </header>
              {liste.map((h) => {
                const f = parId.get(h.foyerId);
                if (!f) return null;
                return (
                  <div key={f.id} className={`carte-hote-draggable${glisse === `${f.id}|${e}` ? ' en-cours' : ''}`} draggable onDragStart={(ev) => onDragStart(ev, f.id, e)} onDragEnd={() => setGlisse(null)}>
                    <CarteHote
                      foyer={f}
                      etapePlacee={e}
                      accordHorsDispo={h.accordHorsDispo}
                      etapesRecues={etapesDe(f.id)}
                      onDeplacer={(cible) => deplacer(f.id, cible, e)}
                      onRecevoirAussi={(aussi) => recevoirAussi(f.id, aussi)}
                    />
                  </div>
                );
              })}
              <div className="zone-depot">
                {liste.length === 0 ? (
                  <>
                    <IconeMaison taille={22} />
                    <span>Il faut au moins 1 hôte</span>
                  </>
                ) : (
                  'Déposer un hôte ici'
                )}
              </div>
              <footer>
                <div className="ligne-titre">
                  <span>
                    <strong>
                      {liste.length} hôte{liste.length > 1 ? 's' : ''}
                    </strong>{' '}
                    · {liste.length} groupe{liste.length > 1 ? 's' : ''}
                    {(() => {
                      const places = capaciteEtape(liste.map((h) => parId.get(h.foyerId)).filter((f): f is Foyer => !!f));
                      return places !== null ? <span className={places < participants ? 'texte-attention' : 'texte-muet'}> · {places} places</span> : null;
                    })()}
                  </span>
                  <strong>{liste.length ? (prev.min === prev.max ? `tables de ${prev.max}` : `tables de ${prev.min} à ${prev.max}`) : '—'}</strong>
                </div>
                {liste.length ? <JaugeTaille taille={prev.max} plage={plageReglee} /> : null}
                <div className="texte-doux">{conseilEtape(participants, liste.length, reserveDe(e), plageReglee, prev)}</div>
              </footer>
            </section>
          );
        })}
      </div>

      {nonPlaces.length > 0 ? (
        <section
          className={`section-reserve${survol === 'reserve' ? ' survol' : ''}`}
          aria-label="Volontaires non placés"
          onDragOver={(ev) => {
            ev.preventDefault();
            if (survol !== 'reserve') setSurvol('reserve');
          }}
          onDragLeave={() => setSurvol(null)}
          onDrop={(ev) => onDrop(ev, null)}
        >
          <IconeMaison taille={24} />
          <div className="pile-6">
            <strong style={{ color: 'var(--warn-fg)' }}>
              {nonPlaces.length} hôte{nonPlaces.length > 1 ? 's' : ''} volontaire{nonPlaces.length > 1 ? 's' : ''} {nonPlaces.length > 1 ? 'ne sont pas utilisés' : "n'est pas utilisé"}
            </strong>
            <span className="texte-doux" style={{ fontSize: 15 }}>
              {nonPlaces.length > 1 ? 'Ils seront convives' : 'Il sera convive'} aux trois étapes.
              {(() => {
                const f = nonPlaces[0]!;
                const m = meilleureEtape(f);
                return m ? ` En ${nonPlaces.length > 1 ? 'les ajoutant' : "l'ajoutant"} ${AU[m]}, les groupes ${m === 'entree' ? "de l'entrée" : `du ${ETAPE_INFO[m].label.toLowerCase()}`} passeraient à ${tailleGroupeAttendue(participants, hotesDe(m).length + 1)} personnes.` : '';
              })()}
            </span>
          </div>
          <div className="cartes">
            {nonPlaces.map((f) => {
              const m = meilleureEtape(f);
              return (
                <div key={f.id} className="carte-hote-draggable" draggable onDragStart={(ev) => onDragStart(ev, f.id)} onDragEnd={() => setGlisse(null)}>
                  <CarteHote foyer={f} onDeplacer={(cible) => deplacer(f.id, cible)} />
                  {m ? (
                    <button type="button" className="btn" onClick={() => deplacer(f.id, m)} disabled={enCours}>
                      Ajouter {AU[m]}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* ---- états d'alerte (planche 4b) ---- */}
      {alertes
        .filter((a) => a.code !== 'pret' && a.code !== 'volontaire_non_utilise')
        .map((a) => {
          const cle = `${a.code}:${a.etape ?? ''}:${a.foyerId ?? ''}`;
          const f = a.foyerId ? parId.get(a.foyerId) : undefined;
          if (a.code === 'etape_sans_hote' && a.etape) {
            const souples = volontaires.filter((x) => x.disponibilites.length >= 2 && !etapeDe(x.id)).concat(volontaires.filter((x) => x.disponibilites.length >= 2 && etapeDe(x.id)));
            const emails = volontaires.map((x) => (x as Foyer & { email?: string | null }).email).filter(Boolean) as string[];
            return (
              <BandeauAlerte
                key={cle}
                niveau="attention"
                role="alert"
                titre={`Personne n'a coché ${LE[a.etape]}.`}
                actions={
                  <>
                    {emails.length ? (
                      <a className="btn principal" href={`mailto:?bcc=${encodeURIComponent(emails.join(','))}&subject=${encodeURIComponent(`${detail.event.nom} : qui pourrait recevoir ${AU[a.etape]} ?`)}`}>
                        Écrire aux volontaires
                      </a>
                    ) : null}
                    <button type="button" className="btn" onClick={() => setDesignation(designation === a.etape ? null : a.etape!)}>
                      Désigner un hôte
                    </button>
                    {designation === a.etape ? (
                      <select
                        className="f-input"
                        style={{ minHeight: 44, width: 'auto' }}
                        aria-label={`Désigner un hôte ${AU[a.etape]}`}
                        defaultValue=""
                        onChange={(ev) => {
                          if (ev.target.value) {
                            deplacer(ev.target.value, a.etape!, undefined, true);
                            setDesignation(null);
                          }
                        }}
                      >
                        <option value="">Choisir une voiture…</option>
                        {volontaires.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.nom}
                            {etapesDe(x.id).length ? ` (hôte ${etapesDe(x.id).map((et) => AU[et]).join(' et ')})` : ''}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </>
                }
              >
                La répartition attendra qu'une table soit trouvée.
                {souples.length ? ` ${souples.map((x) => x.nom).slice(0, 2).join(' et ')} ${souples.length > 1 ? 'ont' : 'a'} coché plusieurs étapes : ${souples.length > 1 ? 'ce sont les plus souples, de bons candidats' : 'un bon candidat'} à solliciter.` : ''}
              </BandeauAlerte>
            );
          }
          if (a.code === 'hote_non_volontaire' && f && a.etape) {
            return (
              <BandeauAlerte
                key={cle}
                niveau="attention"
                role="alert"
                titre={`${f.nom} ne souhaite${f.membres.length > 1 ? 'nt' : ''} plus recevoir.`}
                actions={
                  <>
                    <button type="button" className="btn principal" onClick={() => deplacer(f.id, null)}>
                      Retirer des hôtes
                    </button>
                    <button type="button" className="btn" onClick={() => accorder(f.id)}>
                      Garder {AU[a.etape]}, {f.membres.length > 1 ? 'ils sont' : 'il ou elle est'} d'accord
                    </button>
                  </>
                }
              >
                L'inscription a changé après le placement des hôtes. Tant que ce point n'est pas réglé, la répartition ne peut pas être lancée.
              </BandeauAlerte>
            );
          }
          if (a.code === 'hote_hors_dispo' && f && a.etape) {
            const retour = f.disponibilites[0];
            return (
              <BandeauAlerte
                key={cle}
                niveau="attention"
                role="alert"
                titre={`${f.nom} ${f.membres.length > 1 ? 'ont' : 'a'} indiqué : ${f.disponibilites.map((x) => ETAPE_INFO[x].label.toLowerCase()).join(', ') || 'aucune étape'}${f.disponibilites.length === 1 ? ' uniquement' : ''}.`}
                actions={
                  <>
                    {retour ? (
                      <button type="button" className="btn" onClick={() => deplacer(f.id, retour)}>
                        Remettre {AU[retour]}
                      </button>
                    ) : null}
                    <button type="button" className="btn" onClick={() => accorder(f.id)}>
                      Garder {AU[a.etape]}, {f.membres.length > 1 ? 'ils sont' : 'il est'} d'accord
                    </button>
                  </>
                }
              >
                Vous pouvez {f.membres.length > 1 ? 'les' : 'le'} garder {AU[a.etape]} si vous {f.membres.length > 1 ? 'les' : "l'"}avez prévenu{f.membres.length > 1 ? 's' : ''} ; la carte restera signalée jusqu'à {f.membres.length > 1 ? 'leur' : 'son'} accord.
              </BandeauAlerte>
            );
          }
          if (a.code === 'nombre_hotes' && a.etape) {
            const n = hotesDe(a.etape).length;
            const taille = tailleGroupeAttendue(participants, n);
            const reserve = reserveDe(a.etape);
            const autres = ETAPES.filter((x) => x !== a.etape && reserveDe(x).length > 0);
            return (
              <BandeauAlerte
                key={cle}
                niveau="attention"
                role="alert"
                titre={taille > plage.max ? `Une table de ${taille}, c'est possible, mais ce n'est plus un petit groupe.` : `Des tables de ${taille}, c'est peu.`}
                actions={
                  <>
                    {reserve.length ? (
                      <button type="button" className="btn principal" onClick={() => deplacer(reserve[0]!.id, a.etape!)}>
                        Ajouter {reserve[0]!.nom} {AU[a.etape]}
                      </button>
                    ) : null}
                    <button type="button" className="btn" onClick={() => ignorer(a)}>
                      Continuer avec {taille}
                    </button>
                  </>
                }
              >
                {reserve.length
                  ? `Avec ${n + reserve.length} hôte${n + reserve.length > 1 ? 's' : ''} ${AU[a.etape]}, vous auriez des groupes de ${tailleGroupeAttendue(participants, n + reserve.length)}.`
                  : `Aucun volontaire de réserve ${AU[a.etape]}.`}
                {autres.length ? ` ${autres.map((x) => `${ETAPE_INFO[x].label} a ${reserveDe(x).length} volontaire${reserveDe(x).length > 1 ? 's' : ''} de réserve`).join(', ')}.` : ''}
              </BandeauAlerte>
            );
          }
          if (a.code === 'capacite_insuffisante' && a.etape) {
            const reserve = reserveDe(a.etape);
            return (
              <BandeauAlerte
                key={cle}
                niveau="attention"
                role="alert"
                titre={a.message}
                actions={
                  reserve.length ? (
                    <button type="button" className="btn principal" onClick={() => deplacer(reserve[0]!.id, a.etape!)}>
                      Ajouter {reserve[0]!.nom} {AU[a.etape]}
                    </button>
                  ) : (
                    <Link className="btn" to={`/app/e/${eventId}/participants`}>
                      Revoir les capacités
                    </Link>
                  )
                }
              >
                Les capacités sont celles annoncées par les hôtes à l'inscription, hôtes compris. La répartition ne dépassera une table qu'en dernier recours, et vous le signalera.
              </BandeauAlerte>
            );
          }
          if (a.code === 'hote_absent' && f) {
            return (
              <BandeauAlerte key={cle} niveau="attention" role="alert" titre={a.message} actions={<button type="button" className="btn" onClick={() => deplacer(f.id, null)}>Retirer des hôtes</button>} />
            );
          }
          return (
            <BandeauAlerte key={cle} niveau={a.niveau === 'ok' ? 'ok' : a.niveau} titre={a.message} />
          );
        })}

      {pret ? (
        <>
          <BandeauAlerte niveau="ok" titre="Vous pouvez lancer la répartition." actions={<Link to={`/app/e/${eventId}/repartition`} className="btn principal">Répartir les convives</Link>}>
            3 étapes couvertes{reserves.some((r) => r.niveau === 'attention') ? ', quelques points à regarder.' : ', tous les groupes dans la plage.'} Tables visées : {plage.min} à {plage.max}.
          </BandeauAlerte>
          {reserves.length ? (
            <ul className="liste-reserves">
              {reserves.map((r, i) => (
                <li key={i} className={r.niveau}>
                  {r.texte}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}

      {hotes.length === 0 && volontaires.length > 0 ? (
        <BandeauAlerte niveau="info" titre="Aucun hôte placé pour l'instant.">
          Cliquez sur « Nouvelle proposition », ou glissez les volontaires ci-dessus dans les colonnes.
        </BandeauAlerte>
      ) : null}
      <p className="texte-muet" style={{ fontSize: 13 }}>
        
        Volontaires = voitures présentes ayant accepté de recevoir. L'hôte compte dans son groupe.
      </p>
    </div>
  );
}
